// The state a meeting shows in its identity header and in the meetings table: the summary's
// lifecycle, in the design system's words (05-content §6 — Pending / Running / Completed / Failed),
// plus the absence descriptors ("No transcript", "Not processed", "No summary") for a meeting
// nothing has been started on, each with a glyph so the state is never colour alone. Pure;
// unit-tested under node.

import type { SummaryDto } from '../../../shared/api-types.js';
import { FELLOW_ITEM_STATE_LABELS, type SummaryStatus, type TranscriptStatus } from '../../../shared/enums.js';
import { autoProcessWindowLabel } from '../../../shared/fellow.js';
import type { IconName } from '../../ui/Icon.js';
import type { Tone } from '../../ui/StatusPill.js';

export type SummaryState = SummaryStatus | 'none';

export interface SummaryPresentation {
  /** The pill's word. */
  word: string;
  tone: Tone;
  icon: IconName;
  /** One plain sentence for a title attribute or a description line. */
  detail: string;
  /** Position in the lifecycle for sorting an enum column (04-data §1.2.1). */
  rank: number;
}

/**
 * @param processedAt The meeting's `processedAt`; null means it was imported outside the automatic
 * window and awaits the person (spec §10). Callers without it pass undefined and get the old answer.
 */
export function summaryPresentation(state: SummaryState, transcriptStatus: TranscriptStatus, processedAt?: string | null): SummaryPresentation {
  if (state === 'none' && transcriptStatus === 'empty') {
    return { word: 'No transcript', tone: 'neutral', icon: 'file-text', detail: 'Fellow held no transcript for this recording, so no summary can be generated.', rank: 0 };
  }
  // Why an absence descriptor, not a lifecycle word: like "No summary" and "No transcript" this names
  // what is missing (nothing has been started), so it sits outside 05-content §6's Pending / Running /
  // Completed / Failed vocabulary rather than adding a fifth state to it.
  if (state === 'none' && processedAt === null) {
    return {
      word: 'Not processed',
      tone: 'neutral',
      icon: 'clock',
      detail: `Older than ${autoProcessWindowLabel()} when it was imported. Process it when you want a summary and to-dos.`,
      rank: 1,
    };
  }
  switch (state) {
    case 'queued':
      return { word: 'Pending', tone: 'info', icon: 'clock', detail: 'A summary is waiting to start.', rank: 3 };
    case 'running':
      return { word: 'Running', tone: 'info', icon: 'refresh', detail: 'The summary is being generated now.', rank: 4 };
    case 'ready':
      return { word: 'Completed', tone: 'success', icon: 'check', detail: 'The summary and its to-dos are ready.', rank: 6 };
    case 'failed':
      return { word: 'Failed', tone: 'danger', icon: 'alert-circle', detail: 'The last attempt to generate a summary failed.', rank: 5 };
    case 'none':
    default:
      return { word: 'No summary', tone: 'neutral', icon: 'minus', detail: 'No summary has been generated for this meeting yet.', rank: 2 };
  }
}

/** True when the meeting has a transcript and was left unprocessed at import: the "Process meeting" case. */
export function awaitsProcessing(meeting: { transcriptStatus: TranscriptStatus; processedAt: string | null }): boolean {
  return meeting.transcriptStatus === 'available' && meeting.processedAt === null;
}

/** True while a summary job is in flight, which is when the page polls for its outcome. */
export function summaryInFlight(summary: Pick<SummaryDto, 'status'> | null): boolean {
  return summary !== null && (summary.status === 'queued' || summary.status === 'running');
}

/**
 * Why a summary failed, sorted into the two conditions app-baseline 05-client §5.4 keeps apart:
 * a provider that was never switched on versus one that is failing.
 */
export function summaryFailureKind(summary: Pick<SummaryDto, 'status' | 'errorCode'>): 'not-configured' | 'failing' | null {
  if (summary.status !== 'failed') return null;
  return summary.errorCode === 'ai_not_configured' ? 'not-configured' : 'failing';
}

/** What generated a summary, for the provenance line — the demo summariser is always named as such. */
export function summaryProviderLabel(summary: Pick<SummaryDto, 'provider' | 'model' | 'isDemo'>): string {
  if (summary.isDemo) return 'Demo summariser';
  const provider = summary.provider === 'anthropic' ? 'Anthropic Claude' : summary.provider;
  return summary.model ? `${provider} (${summary.model})` : provider;
}

// --- Fellow's own notes: an item's status as Fellow sent it --------------------------------------------------------

/**
 * Fellow's status for an item in its own notes, said in Calis's words — "Completed in Fellow", "Open in Fellow", "Archived
 * in Fellow" (`FELLOW_ITEM_STATE_LABELS`) — or, for a status these do not cover, Fellow's own word quoted and attributed;
 * null when Fellow sent none. Why not the raw value (05-content §6: "Done" is on Completed's Not-list, and §6.1: "A stored
 * code is not a word"; the owner's ruling Q9 of escalation 20261005-200526, "Completed in Fellow" everywhere — Fellow's
 * raw "Done" status included; Phase 1c R14): the disclosure printed "Done" and "Incomplete" as Fellow stored them. The
 * done set is the server's own (`isFellowItemDone`, src/server/fellow/normalise.ts), held equal by
 * test/server/client-pages/fellow-note-status.test.ts.
 */
export function fellowNoteItemStatus(raw: string | null): string | null {
  const status = raw?.trim() ?? '';
  if (status === '') return null;
  if (/^(done|complete|completed|closed|resolved)$/iu.test(status)) return FELLOW_ITEM_STATE_LABELS.done;
  // The open set is the one the server's Fellow client reads as open (`actionItemStatus`, src/server/fellow/client.ts).
  if (/^(incomplete|open)$/iu.test(status)) return FELLOW_ITEM_STATE_LABELS.open;
  if (/^archived$/iu.test(status)) return FELLOW_ITEM_STATE_LABELS.archived;
  // Why quoted and attributed rather than dropped: a status Calis has no word for is still Fellow's to tell, and it is
  // said as Fellow's, never as Calis's own state.
  return `Fellow’s status: “${status}”`;
}
